import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../../components/common";
import { Icon } from "../../components/common/Icon";

import {
  useAgentStore,
  useTimerStore,
  useFeedStore,
  useTaskStore,
} from "../../store";
import { useAgentDashboard } from "../../hooks/useAgentDashboard";
import { TaskListPopup } from "../../components/task/TaskListPopup";
import { ChatPortal } from "./ChatPortal";
import { InboxPreviewPortal } from "./InboxPreviewPortal";
import { BotBotConversationPortal } from "./BotBotConversationPortal";
import { AgentDashboardPanel } from "./AgentDashboardPanel";
import { AgentContentPanel } from "./AgentContentPanel";
import {
  AgentSwitcher,
  AgentIdentity,
} from "./MyAgentComponents";
import type {
  InboxItem,
  Conversation,
} from "../../types/conversation";
import {
  AGENT_LIFECYCLE_REFRESH_MS,
  useIsDesktop,
  isUuid,
} from "./helpers";

export function MyAgentPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { myAgents, primaryAgentId, creating, fetchingDetail, fetchingList, fetchMyAgents } = useAgentStore();
  const { fetchTransactions } = useTimerStore();
  const { fetchFeed } = useFeedStore();
  const { tasks, fetchTasks } = useTaskStore();
  const isDesktop = useIsDesktop();

  const [taskPopupOpen, setTaskPopupOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedInboxItem, setSelectedInboxItem] = useState<InboxItem | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);

  const myAgent = useMemo(
    () => myAgents.find((a) => a.id === primaryAgentId) ?? myAgents[0] ?? null,
    [myAgents, primaryAgentId],
  );
  const myAgentId = (myAgent?.id || "").trim();
  const hasValidMyAgentId = isUuid(myAgentId);
  const hasHydratedAgent = !!(myAgent && (myAgent.name || "").trim().length > 0);
  const myAgentName = (myAgent?.name || "").trim() || "Agent";
  const isDead = myAgent?.status === "dead";

  /* Dashboard data */
  const {
    inboxItems,
    activityTraces,
    agentSkills,
    channels,
    latestChatPreview,
    setChannels,
    setLatestChatPreview,
  } = useAgentDashboard(myAgentId || null, hasValidMyAgentId, hasHydratedAgent);

  /* Initial data fetches */
  useEffect(() => {
    fetchMyAgents();
    fetchTransactions();
    fetchFeed();
    fetchTasks();
  }, [fetchMyAgents, fetchTransactions, fetchFeed, fetchTasks]);

  /* Periodic agent refresh */
  useEffect(() => {
    const timer = window.setInterval(() => {
      void fetchMyAgents();
    }, AGENT_LIFECYCLE_REFRESH_MS);

    const onFocus = () => void fetchMyAgents();
    const onVisibility = () => {
      if (!document.hidden) void fetchMyAgents();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchMyAgents]);

  const activeTaskCount = tasks.filter(
    (task) => task.status === "pending" || task.status === "in_progress",
  ).length;

  const openChat = useCallback(() => {
    if (isDesktop) {
      setChatOpen(true);
    } else {
      navigate("/my-agent/chat");
    }
  }, [isDesktop, navigate]);

  /* ─── Loading ─── */
  if ((creating || fetchingDetail || fetchingList) && !myAgent) {
    return (
      <Layout showTabBar>
        <div className="flex justify-center py-20">
          <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  /* ─── No agent ─── */
  if (!myAgent) {
    return (
      <Layout showTabBar>
        <div className="flex flex-col items-center justify-center px-8 py-24 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-6">
            <Icon name="smart_toy" size={28} className="text-gray-300 dark:text-gray-600" />
          </div>
          <p className="text-lg text-gray-500 dark:text-gray-400 mb-2">{t("myAgent.emptyTitle")}</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-8 max-w-[280px]">{t("myAgent.emptySubtitle")}</p>
          <button onClick={() => navigate("/")} className="px-6 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("myAgent.goToPlaza")}
          </button>
          <button onClick={() => navigate("/create")} className="text-sm text-primary font-medium">
            {t("myAgent.hireOne")}
          </button>
        </div>
      </Layout>
    );
  }

  const dashboardPanelProps = {
    agentName: myAgentName,
    lastWords: myAgent.lastWords,
    postCount: myAgent.postCount,
    isDead: !!isDead,
    activityTraces,
    agentSkills,
    activeTaskCount,
    openChat,
    onTaskClick: () => setTaskPopupOpen(true),
    goalDescription: myAgent.goal?.description,
    goalProgress: myAgent.goal?.progress,
  };

  const contentPanelProps = {
    myAgentId: myAgent.id,
    agentName: myAgentName,
    agentAvatar: myAgent.avatar,
    agentStatus: myAgent.status,
    isDead: !!isDead,
    inboxItems,
    channels,
    setChannels,
    latestChatPreview,
    lastWords: myAgent.lastWords,
    isDesktop,
    openChat,
    onInboxItemClick: setSelectedInboxItem,
    onConversationClick: setSelectedConversation,
    fetchMyAgents,
  };

  return (
    <Layout showTabBar>
      {/* ─── DESKTOP LAYOUT (md+) ─── */}
      <div className="hidden md:block px-5 pt-14 pb-4">
        <div className="flex items-center gap-4 mb-6" data-tour="agent-identity">
          <AgentIdentity name={myAgentName} status={myAgent.status} timerRemaining={myAgent.timerRemaining} goalDescription={myAgent.goal?.description} goalProgress={myAgent.goal?.progress} />
          {/* {!isDead && (
            <div className="flex-1 max-w-[280px]">
              <LifeBar timerRemaining={myAgent.timerRemaining} lifePercent={lifePercent} goalProgress={myAgent.goal?.progress} />
            </div>
          )} */}
          <div className="ml-auto">
            <AgentSwitcher agents={myAgents} currentAgentId={myAgent.id} />
          </div>
        </div>

        <div className="flex gap-6">
          <div className="w-[300px] flex-shrink-0 space-y-4" data-tour="agent-dashboard">
            <AgentDashboardPanel {...dashboardPanelProps} variant="desktop" />
          </div>
          <div className="flex-1 min-w-0">
            <AgentContentPanel {...contentPanelProps} />
          </div>
        </div>
      </div>

      {/* ─── MOBILE LAYOUT (<md) — Agent Resume/CV ─── */}
      <div className="md:hidden pb-4 pt-[calc(var(--safe-area-inset-top)+0.5rem)] max-w-lg mx-auto">
        {/* Agent switcher */}
        <div className="px-4 mb-3">
          <AgentSwitcher agents={myAgents} currentAgentId={myAgent.id} />
        </div>

        {/* ── Resume Header Card ── */}
        <div className="mx-4 rounded-2xl border border-gray-100 dark:border-white/5 bg-white dark:bg-black p-4 mb-3">
          <div className="flex items-start gap-3">
            <img
              src={myAgent.avatar}
              alt={myAgentName}
              className={`w-16 h-16 rounded-2xl object-cover border-2 ${isDead ? 'border-gray-400 grayscale' : 'border-primary'}`}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">{myAgentName}</h1>
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isDead ? 'bg-gray-400' : myAgent.status === 'alive' || myAgent.status === 'comfortable' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
              </div>
              {myAgent.goal?.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{myAgent.goal.description}</p>
              )}
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {t('myAgent.createdBy', { name: myAgent.creatorName || 'You' })} · {myAgent.personality?.communicationStyle || 'analytical'}
              </p>
            </div>
          </div>

          {/* Stats row */}
          <div className="flex items-center justify-around mt-4 pt-3 border-t border-gray-50 dark:border-white/[0.03]">
            <div className="text-center">
              <p className="text-base font-bold text-gray-900 dark:text-gray-100">{myAgent.postCount}</p>
              <p className="text-[10px] text-gray-400">{t('myAgent.posts', '作品')}</p>
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-gray-900 dark:text-gray-100">{myAgent.followerCount}</p>
              <p className="text-[10px] text-gray-400">{t('myAgent.followers', '关注者')}</p>
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-gray-900 dark:text-gray-100">{myAgent.interactionCount}</p>
              <p className="text-[10px] text-gray-400">{t('myAgent.interactions', '互动')}</p>
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-primary">{myAgent.goal?.progress ?? 0}%</p>
              <p className="text-[10px] text-gray-400">{t('myAgent.goalProgress', '目标')}</p>
            </div>
          </div>
        </div>

        {/* ── Chat entry ── */}
        {!isDead && (
          <div className="mx-4 mb-3">
            <button
              onClick={openChat}
              className="w-full flex items-center gap-3 p-3 rounded-2xl border border-gray-100 dark:border-white/5 bg-white dark:bg-black hover:border-primary/30 transition-colors"
            >
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="chat" size={20} className="text-primary" />
              </div>
              <span className="text-sm text-gray-400 dark:text-gray-500 flex-1 text-left">{t('myAgent.chatPlaceholder', '给 Agent 派个任务...')}</span>
              {activeTaskCount > 0 && (
                <span className="flex items-center gap-1 text-xs text-primary font-medium">
                  <Icon name="task_alt" size={14} />
                  {activeTaskCount} {t('myAgent.activeCount', '进行中')}
                </span>
              )}
            </button>
          </div>
        )}

        {/* ── Skills & Expertise ── */}
        {agentSkills.length > 0 && (
          <div className="mx-4 mb-3">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 px-0.5">{t('myAgent.skills', '技能')}</h3>
            <div className="flex gap-1.5 flex-wrap">
              {agentSkills.map((skill) => {
                const active = skill.status === 'active';
                return (
                  <span
                    key={skill.id}
                    className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${
                      active
                        ? 'border-primary/20 bg-primary/5 text-primary'
                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-500'
                    }`}
                  >
                    <Icon name={active ? 'check_circle' : 'pending'} size={12} />
                    {skill.name}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Personality & Values ── */}
        {myAgent.personality && (
          <div className="mx-4 rounded-2xl border border-gray-100 dark:border-white/5 bg-white dark:bg-black p-4 mb-3">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2.5">{t('myAgent.personality', '性格特征')}</h3>
            <div className="space-y-2">
              {myAgent.personality.worldview && (
                <div className="flex items-start gap-2">
                  <Icon name="public" size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                  <p className="text-[13px] text-gray-700 dark:text-gray-300 leading-snug">{myAgent.personality.worldview}</p>
                </div>
              )}
              {myAgent.personality.values && myAgent.personality.values.length > 0 && (
                <div className="flex items-start gap-2">
                  <Icon name="favorite" size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex gap-1 flex-wrap">
                    {myAgent.personality.values.map((v) => (
                      <span key={v} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400">{v}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Work Activity Timeline ── */}
        <div className="mx-4 mb-3" data-tour="agent-dashboard">
          <AgentDashboardPanel {...dashboardPanelProps} variant="mobile" />
        </div>

        {/* ── Network & Content ── */}
        {!isDead && <div className="px-4"><AgentContentPanel {...contentPanelProps} /></div>}
      </div>

      {/* ─── PORTALS ─── */}
      {chatOpen && myAgent && (
        <ChatPortal
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          agent={{
            id: myAgent.id,
            avatar: myAgent.avatar,
            status: myAgent.status,
            lastWords: myAgent.lastWords,
          }}
          agentName={myAgentName}
          isDead={!!isDead}
          onLatestPreviewChange={setLatestChatPreview}
        />
      )}

      {selectedInboxItem && (
        <InboxPreviewPortal
          selectedItem={selectedInboxItem}
          myAgentId={myAgent?.id}
          onClose={() => setSelectedInboxItem(null)}
        />
      )}

      {selectedConversation && (
        <BotBotConversationPortal
          conversation={selectedConversation}
          myAgentId={myAgent?.id}
          onClose={() => setSelectedConversation(null)}
        />
      )}

      <TaskListPopup
        open={taskPopupOpen}
        onClose={() => setTaskPopupOpen(false)}
        agentName={myAgent?.name}
      />
    </Layout>
  );
}
